import { companyManagement } from '@/services/companyManagement';
import { delay, extractErrorMessage } from '@/services/utils';
import { buildBeneficiaryUpdatePayload, buildDependentUpdatePayload } from '@/utils/beneficiary';
import {
	getRmsConsultaPayload,
	getRmsDisplayName,
	getRmsReprocessPayload,
	parseRmsStatus,
} from '@/utils/rms';
import { buildSectorFilterParams, DEFAULT_SECTOR_FILTER } from '@/utils/sectors';
import {
	initialDialog,
	initialToast,
	initialUserDetailsModal,
	PERSISTENCE_DELAY_MS,
} from '@/utils/storeDefaults';

export default {
	namespaced: true,

	state: {
		beneficiaries: [],
		totalItems: 0,
		companyId: null,

		options: { page: 1, itemsPerPage: 10 },
		sectorFilter: DEFAULT_SECTOR_FILTER,
		isSearchActive: false,
		refreshKey: 0,

		loading: false,
		expandedItems: [],

		selectedBeneficiary: null,
		selectedDependent: null,
		selectedOrderId: null,

		dialog: { ...initialDialog },
		rmsDialog: { ...initialDialog },
		toast: { ...initialToast },
		userDetailsModal: { ...initialUserDetailsModal },
		rmsLoading: null,
		selectedRmsItem: null,

		nr1Loading: false,
		pendingNr1: null,
	},

	getters: {
		beneficiaries: (state) => state.beneficiaries,
		totalItems: (state) => state.totalItems,
		companyId: (state) => state.companyId,
		options: (state) => state.options,
		sectorFilter: (state) => state.sectorFilter,
		isSearchActive: (state) => state.isSearchActive,
		refreshKey: (state) => state.refreshKey,
		loading: (state) => state.loading,
		expandedItems: (state) => state.expandedItems,
		dialog: (state) => state.dialog,
		rmsDialog: (state) => state.rmsDialog,
		rmsLoading: (state) => state.rmsLoading,
		toast: (state) => state.toast,
		userDetailsModal: (state) => state.userDetailsModal,
		nr1Loading: (state) => state.nr1Loading,
	},

	mutations: {
		SET_COMPANY_ID(state, companyId) {
			state.companyId = companyId;
		},
		SET_BENEFICIARIES(state, { items, total }) {
			state.beneficiaries = items;
			state.totalItems = total;
		},
		SET_OPTIONS(state, options) {
			state.options = options;
		},
		SET_SECTOR_FILTER(state, value) {
			state.sectorFilter = value;
		},
		SET_IS_SEARCH_ACTIVE(state, value) {
			state.isSearchActive = value;
		},
		INCREMENT_REFRESH_KEY(state) {
			state.refreshKey += 1;
		},
		SET_LOADING(state, value) {
			state.loading = value;
		},
		SET_EXPANDED_ITEMS(state, items) {
			state.expandedItems = items;
		},
		SET_SELECTED_BENEFICIARY(state, beneficiary) {
			state.selectedBeneficiary = beneficiary;
		},
		SET_SELECTED_DEPENDENT(state, dependent) {
			state.selectedDependent = dependent;
		},
		SET_SELECTED_ORDER_ID(state, orderId) {
			state.selectedOrderId = orderId;
		},
		SET_DIALOG(state, dialog) {
			state.dialog = { ...initialDialog, ...dialog };
		},
		CLOSE_DIALOG(state) {
			state.dialog.show = false;
		},
		SET_TOAST(state, toast) {
			state.toast = { ...initialToast, ...toast };
		},
		HIDE_TOAST(state) {
			state.toast.show = false;
		},
		SET_USER_DETAILS_MODAL(state, modal) {
			state.userDetailsModal = { ...initialUserDetailsModal, ...modal };
		},
		CLOSE_USER_DETAILS_MODAL(state) {
			state.userDetailsModal.show = false;
		},
		SET_RMS_LOADING(state, id) {
			state.rmsLoading = id;
		},
		SET_SELECTED_RMS_ITEM(state, item) {
			state.selectedRmsItem = item;
		},
		SET_RMS_DIALOG(state, dialog) {
			state.rmsDialog = { ...initialDialog, ...dialog };
		},
		CLOSE_RMS_DIALOG(state) {
			state.rmsDialog.show = false;
		},
		SET_NR1_LOADING(state, value) {
			state.nr1Loading = value;
		},
		SET_PENDING_NR1(state, value) {
			state.pendingNr1 = value;
		},
		UPDATE_BENEFICIARY_RMS_STATUS(state, { itemId, rmsStatus }) {
			state.beneficiaries = state.beneficiaries.map((beneficiary) => {
				if (beneficiary.id === itemId) {
					return { ...beneficiary, rmsStatus };
				}
				if (beneficiary.dependents?.some((dependent) => dependent.id === itemId)) {
					return {
						...beneficiary,
						dependents: beneficiary.dependents.map((dependent) =>
							dependent.id === itemId ? { ...dependent, rmsStatus } : dependent
						),
					};
				}
				return beneficiary;
			});
		},
	},

	actions: {
		showToast({ commit }, toast) {
			commit('SET_TOAST', { show: true, ...toast });
			setTimeout(() => commit('HIDE_TOAST'), 3000);
		},

		setCompanyId({ commit }, companyId) {
			commit('SET_COMPANY_ID', companyId);
		},

		async fetchBeneficiaries({ commit, state, dispatch }) {
			if (!state.companyId) return;

			commit('SET_LOADING', true);
			commit('SET_IS_SEARCH_ACTIVE', false);

			try {
				const { page, itemsPerPage } = state.options;
				const offset = (page - 1) * itemsPerPage;
				const { data } = await companyManagement.getBeneficiariesByCompany(state.companyId, {
					limit: itemsPerPage,
					offset,
					...buildSectorFilterParams(state.sectorFilter),
				});
				commit('SET_BENEFICIARIES', {
					items: data?.items || [],
					total: data?.total || 0,
				});
			} catch (error) {
				commit('SET_BENEFICIARIES', { items: [], total: 0 });
				dispatch('showToast', {
					type: 'error',
					message: 'Não foi possível carregar os beneficiários da empresa.',
				});
				throw error;
			} finally {
				commit('SET_LOADING', false);
			}
		},

		updateOptions({ commit, dispatch, state }, options) {
			if (state.isSearchActive) {
				commit('SET_IS_SEARCH_ACTIVE', false);
				return;
			}
			commit('SET_OPTIONS', options);
			if (state.companyId) dispatch('fetchBeneficiaries');
		},

		async setSectorFilter({ commit, dispatch, state }, sectorFilter) {
			commit('SET_SECTOR_FILTER', sectorFilter);
			commit('SET_OPTIONS', { ...state.options, page: 1 });
			if (state.companyId) await dispatch('fetchBeneficiaries');
		},

		async resetFilters({ commit, dispatch, state }) {
			commit('SET_IS_SEARCH_ACTIVE', false);
			commit('SET_SECTOR_FILTER', DEFAULT_SECTOR_FILTER);
			commit('SET_OPTIONS', { page: 1, itemsPerPage: 10 });
			commit('INCREMENT_REFRESH_KEY');
			if (state.companyId) await dispatch('fetchBeneficiaries');
		},

		setBeneficiariesFromSearch({ commit }, { users, totalItems }) {
			commit('SET_IS_SEARCH_ACTIVE', true);
			commit('SET_OPTIONS', { page: 1, itemsPerPage: 10 });
			commit('SET_BENEFICIARIES', { items: users, total: totalItems });
		},

		async searchBeneficiaryByDocument({ commit, state, dispatch }, { document }) {
			if (!state.companyId) {
				dispatch('showToast', {
					type: 'error',
					message: 'Empresa não carregada. Selecione uma empresa antes de buscar.',
				});
				return [];
			}
			commit('SET_LOADING', true);
			try {
				const { data } = await companyManagement.getBeneficiariesByCompany(state.companyId, { document });
				return data?.items || [];
			} finally {
				commit('SET_LOADING', false);
			}
		},

		setExpandedItems({ commit }, items) {
			commit('SET_EXPANDED_ITEMS', items);
		},

		openConfirmDialog({ commit }, { icon, title, text, actionType, beneficiary, dependent, orderId, hasNr1 }) {
			if (beneficiary) commit('SET_SELECTED_BENEFICIARY', beneficiary);
			if (dependent) commit('SET_SELECTED_DEPENDENT', dependent);
			if (orderId) commit('SET_SELECTED_ORDER_ID', orderId);
			if (typeof hasNr1 === 'boolean') commit('SET_PENDING_NR1', hasNr1);
			commit('SET_DIALOG', { show: true, icon, title, text, actionType });
		},

		closeDialog({ commit }) {
			commit('CLOSE_DIALOG');
		},

		openUserDetailsModal({ commit }, { user, mode = 'view', itemType = 'titular' }) {
			commit('SET_USER_DETAILS_MODAL', { show: true, mode, user, itemType });
		},

		closeUserDetailsModal({ commit }) {
			commit('CLOSE_USER_DETAILS_MODAL');
		},

		openRmsDialog({ commit }, item) {
			commit('SET_SELECTED_RMS_ITEM', item);
			commit('SET_RMS_DIALOG', {
				show: true,
				icon: 'mdi-account-sync-outline',
				title: 'Reprocessar RMS',
				text: `O reprocessamento de ${getRmsDisplayName(item)} pode demorar até 15 minutos para ser concluído. Deseja continuar?`,
			});
		},

		closeRmsDialog({ commit }) {
			commit('CLOSE_RMS_DIALOG');
		},

		async confirmRmsDialog({ state, dispatch, commit }) {
			await dispatch('reprocessRms', state.selectedRmsItem);
			commit('CLOSE_RMS_DIALOG');
		},

		async refreshRmsStatus({ commit, dispatch }, item) {
			commit('SET_RMS_LOADING', item.id);
			try {
				const payload = getRmsConsultaPayload(item);
				if (!payload) {
					dispatch('showToast', { type: 'error', message: 'Dados insuficientes para consultar status na RMS.' });
					return;
				}
				const { data } = await companyManagement.checkRmsStatus(payload);
				const rmsStatus = parseRmsStatus(data);
				commit('UPDATE_BENEFICIARY_RMS_STATUS', { itemId: item.id, rmsStatus });
			} catch (error) {
				dispatch('showToast', { type: 'error', message: 'Não foi possível atualizar o usuário na RMS. Tente novamente!' });
				throw error;
			} finally {
				commit('SET_RMS_LOADING', null);
			}
		},

		async reprocessRms({ commit, dispatch }, item) {
			commit('SET_RMS_LOADING', item.id);
			try {
				const { data } = await companyManagement.reprocessRms(getRmsReprocessPayload(item));
				if (!data) {
					dispatch('showToast', { type: 'error', message: 'Resposta inesperada da RMS. Tente novamente!' });
					return;
				}
				await delay(3000);
				await dispatch('refreshRmsStatus', item);
				dispatch('showToast', {
					type: 'success',
					message: `Usuário: ${getRmsDisplayName(item)}, reprocessado na RMS com sucesso!`,
				});
			} catch (error) {
				dispatch('showToast', { type: 'error', message: 'Não foi possível reprocessar o usuário na RMS. Tente novamente!' });
				throw error;
			} finally {
				commit('SET_RMS_LOADING', null);
			}
		},

		async updateUserDetails({ commit, dispatch, state }, { data, sectorId }) {
			const { itemType, user } = state.userDetailsModal;
			const payload = itemType === 'dependent'
				? buildDependentUpdatePayload(data)
				: buildBeneficiaryUpdatePayload(data);

			const shouldAssignSector = itemType !== 'dependent' && !!sectorId && sectorId !== user.sectorId;
			let sectorFailed = false;

			commit('SET_LOADING', true);
			try {
				if (itemType === 'dependent') {
					await companyManagement.updateDependent(user.id, payload);
				} else {
					await companyManagement.updateBeneficiary(user.id, payload);
				}

				if (shouldAssignSector) {
					try {
						await dispatch('sectorsModule/assignSector', {
							companyId: state.companyId,
							beneficiaryId: user.id,
							sectorId,
						}, { root: true });
					} catch (sectorError) {
						console.warn('[companyManagementModule/updateUserDetails] setor', sectorError);
						sectorFailed = true;
					}
				}

				await delay(PERSISTENCE_DELAY_MS);
				await dispatch('fetchBeneficiaries');
				if (shouldAssignSector) dispatch('sectorsModule/refreshSectors', null, { root: true });
				commit('CLOSE_USER_DETAILS_MODAL');
				dispatch('showToast', sectorFailed
					? { type: 'warning', message: 'Dados atualizados, mas não foi possível alterar o setor.' }
					: { type: 'success', message: 'Usuário atualizado com sucesso!' });
			} catch (error) {
				dispatch('showToast', {
					type: 'error',
					message: 'Não foi possível atualizar o usuário. Tente novamente!',
				});
				throw error;
			} finally {
				commit('SET_LOADING', false);
			}
		},

		async confirmDialog({ state, dispatch, commit }) {
			const { actionType } = state.dialog;
			// fecha antes da requisição para o Confirmar não ficar clicável durante o processamento
			commit('CLOSE_DIALOG');
			if (actionType === 'removeBeneficiary') {
				await dispatch('removeBeneficiary', state.selectedBeneficiary);
			}
			if (actionType === 'removeDependent') {
				await dispatch('removeDependent', {
					dependent: state.selectedDependent,
					orderId: state.selectedOrderId,
				});
			}
			if (actionType === 'toggleNr1') {
				await dispatch('toggleCompanyNr1');
			}
		},

		async updateCompanyNr1(_, { companyId, hasNr1 }) {
			await companyManagement.updateCompanyNr1(companyId, hasNr1);
		},

		async toggleCompanyNr1({ commit, dispatch, state }) {
			if (state.nr1Loading) return;

			const hasNr1 = state.pendingNr1;
			commit('SET_NR1_LOADING', true);
			try {
				await dispatch('updateCompanyNr1', { companyId: state.companyId, hasNr1 });
				await delay(PERSISTENCE_DELAY_MS);
				dispatch('showToast', {
					type: 'success',
					message: hasNr1
						? 'Módulo de NR-1 liberado para esta empresa.'
						: 'Módulo de NR-1 desativado para esta empresa.',
				});
			} catch (error) {
				dispatch('showToast', {
					type: 'error',
					message: extractErrorMessage(error, 'Não foi possível alterar o NR-1 da empresa. Tente novamente.'),
				});
				throw error;
			} finally {
				commit('SET_NR1_LOADING', false);
			}
		},

		async removeBeneficiary({ dispatch, commit, state }, beneficiary) {
			commit('SET_LOADING', true);
			try {
				const beneficiaryId = beneficiary?.user?.id;
				const companyId = beneficiary?.companyId || state.companyId;
				await companyManagement.removeBeneficiary({ companyId, beneficiaryId });
				await delay(PERSISTENCE_DELAY_MS);
				await dispatch('fetchBeneficiaries');
				dispatch('showToast', {
					type: 'success',
					message: 'Beneficiário removido com sucesso.',
				});
			} catch (error) {
				dispatch('showToast', {
					type: 'error',
					message: 'Não foi possível remover o beneficiário. Tente novamente.',
				});
				throw error;
			} finally {
				commit('SET_LOADING', false);
			}
		},

		async removeDependent({ dispatch, commit }, { dependent, orderId }) {
			commit('SET_LOADING', true);
			try {
				await companyManagement.removeDependent({
					orderId,
					dependentId: dependent?.id,
				});
				await delay(PERSISTENCE_DELAY_MS);
				await dispatch('fetchBeneficiaries');
				dispatch('showToast', {
					type: 'success',
					message: 'Dependente removido com sucesso.',
				});
			} catch (error) {
				dispatch('showToast', {
					type: 'error',
					message: 'Não foi possível remover o dependente. Tente novamente.',
				});
				throw error;
			} finally {
				commit('SET_LOADING', false);
			}
		},
	},
};
